/**
 * ASSIST-01 — the Assist RPCs the listing card uses.
 *
 * PostgREST through `apiRpc` with the caller's token from
 * `getValidAccessToken()` (never supabase-js: this app keeps its session under
 * its own localStorage keys). Every function resolves; none throws. Parsing
 * and error mapping live in src/lib/assist/config.ts, where `tsx` tests can
 * reach them.
 */
import { apiRpc } from './client';
import { getValidAccessToken } from './authApi';
import {
  classifyAssistError,
  parseAssistPublicConfig,
  parseMineResult,
  parseRegisterResult,
  type AssistMineOutcome,
  type AssistPublicConfig,
  type AssistRegisterOutcome,
} from '../../src/lib/assist/config';

/**
 * `get_assist_public_config()`, callable by guests (brief R1). Returns null
 * when the call fails, so the caller can retry later instead of caching `off`.
 */
export async function fetchAssistPublicConfig(
  accessToken: string | null,
): Promise<AssistPublicConfig | null> {
  try {
    const { data, error } = await apiRpc<unknown>(
      'get_assist_public_config',
      {},
      accessToken ?? undefined,
    );
    if (error) return null;
    return parseAssistPublicConfig(data);
  } catch {
    return null;
  }
}

/** `register_assist_interest(listing_id)`: join the waitlist (idempotent). */
export async function registerAssistInterest(listingId: string): Promise<AssistRegisterOutcome> {
  try {
    const token = await getValidAccessToken();
    if (!token) return { ok: false, failure: 'auth' };
    const { data, error } = await apiRpc<unknown>(
      'register_assist_interest',
      { listing_id: listingId },
      token,
    );
    if (error) return { ok: false, failure: classifyAssistError(error.message) };
    return parseRegisterResult(data);
  } catch {
    return { ok: false, failure: 'failed' };
  }
}

/** `get_my_assist_interest()`: has the signed-in user joined? */
export async function fetchMyAssistInterest(): Promise<AssistMineOutcome> {
  try {
    const token = await getValidAccessToken();
    if (!token) return { ok: false, failure: 'auth' };
    const { data, error } = await apiRpc<unknown>('get_my_assist_interest', {}, token);
    if (error) return { ok: false, failure: classifyAssistError(error.message) };
    return parseMineResult(data);
  } catch {
    return { ok: false, failure: 'failed' };
  }
}
